import { z } from 'zod';
import { pdfFieldSchema } from '../steps/pdf/template.js';
import { isStepTypeId } from '../steps/registry.js';
import { stepDefinitionSchema, stepDefinitionVersionSchema } from '../steps/definitions.js';
/**
 * The reviewed source PDF of an official form (#188, ADR-009 §2, ADR-014 §1).
 *
 * The bytes are NOT in the request. HR uploads the PDF through quarantine first (the HR upload
 * intent under `catalog/template-sources`), and publishing names that upload. It is published only
 * once its malware verdict is `clean`, and committed to `records/templates` through the store port.
 * The base64 body this replaced wrote unscanned bytes straight into storage — a side door.
 */
export const formSourceSchema = z.object({
    fileName: z.string().trim().min(1).max(200),
    sourceUrl: z.string().url().refine((value) => ['vigeohealth2.sharepoint.com', 'vigeohealth2-my.sharepoint.com'].includes(new URL(value).hostname), 'Use the approved VIGEO SharePoint source'),
    sourceItemId: z.string().min(1).max(200),
    sourceVersion: z.string().min(1).max(200),
    /** The digest HR reviewed. The upload's accepted digest must be exactly this. */
    sha256: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    /** The quarantine upload holding the source — or its composite with an appended signature page. */
    uploadId: z.string().uuid(),
    fields: z.array(pdfFieldSchema).max(200),
}).strict();
/**
 * Appending a signature page to a clean source (#188). The composite is SERVER-GENERATED bytes, so
 * it is staged back into quarantine as its own upload and scanned before anything can publish it
 * (ADR-009 §2: one path, no trusted side door). Publishing then names the composite's upload.
 */
export const signaturePageInputSchema = z.object({
    heading: z.string().trim().min(1).max(200),
    acknowledgment: z.string().trim().min(1).max(8000),
}).strict();
export const signaturePageResultSchema = z.object({
    /** The composite's upload. Publish it once its scan is `clean`. */
    uploadId: z.string().uuid(),
    sha256: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    /** The appended page's fields, to add to the source's own mapping. */
    fields: z.array(pdfFieldSchema),
    signatureField: z.string().min(1),
    acceptanceField: z.string().min(1),
}).strict();
export const publishFormSchema = z.object({
    definitionId: z.string().regex(/^[a-z0-9][a-z0-9-]{2,100}$/),
    expectedVersion: z.number().int().nonnegative(),
    type: z.string().refine(isStepTypeId, 'Choose a registered step type'),
    heading: z.string().trim().min(1).max(200),
    helpText: z.string().max(4000).default(''),
    config: z.record(z.unknown()),
    satisfies: z.array(z.string().min(1)).max(50).default([]),
    contribution: z.enum(['completes', 'contributes']).default('completes'),
    eligibleJobProfileKeys: z.array(z.string().min(1)).max(50).default([]),
    controlledForm: z.boolean().default(true),
    autoIssue: z.boolean().default(false),
    source: formSourceSchema.optional(),
    reason: z.string().trim().min(1).max(2000),
}).strict();
export const authoredFormSchema = z.object({ head: stepDefinitionSchema, version: stepDefinitionVersionSchema });
export const authoredFormsSchema = z.object({ forms: z.array(authoredFormSchema) });
/** Initial authoring suggestions; every published form stores its chosen destination. */
export const formAuthoringDefaults = { target: 'employee-records' };
//# sourceMappingURL=authoring.js.map